import React, { useState, useEffect, useRef } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { FaCommentDots } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import "./app.css";

const API_BASE = process.env.REACT_APP_ML_URL;
const BACKEND_URL = process.env.REACT_APP_URL;

ChartJS.register(ArcElement, Tooltip, Legend);



// Returns Authorization header with JWT token if logged in
function getAuthHeaders(extra = {}) {
    const token = localStorage.getItem("token");
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...extra,
    };
}

export default function DetectorApp() {
    const navigate = useNavigate();
    const [inputText, setInputText] = useState("");
    const [status, setStatus] = useState("");
    const [activeTab, setActiveTab] = useState("ai");

    // AI State
    const [label, setLabel] = useState("");
    const [confidence, setConfidence] = useState("");
    const [resultColor, setResultColor] = useState("");

    // Plagiarism State
    const [plagScore, setPlagScore] = useState(null);
    const [plagMatches, setPlagMatches] = useState([]);
    const [visibleMatches, setVisibleMatches] = useState(5);

    const [loading, setLoading] = useState(false);
    const [darkMode, setDarkMode] = useState(false);
    const [selectedFileName, setSelectedFileName] = useState("");
    const [userName, setUserName] = useState("");
    const [isAdmin, setIsAdmin] = useState(false);

    // Feedback State
    const [liked, setLiked] = useState(false);
    const [disliked, setDisliked] = useState(false);
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [comment, setComment] = useState("");
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

    const [inputMode, setInputMode] = useState("text");

    const textareaRef = useRef(null);
    const API_URL = API_BASE;

    useEffect(() => {
        const syncTheme = () => {
            const savedMode = localStorage.getItem("darkMode");
            setDarkMode(savedMode ? JSON.parse(savedMode) : false);
        };

        syncTheme(); // Initial sync
        window.addEventListener("themeChanged", syncTheme);

        const name = localStorage.getItem("userName");
        if (name) setUserName(name);

        // Check if user is admin
        const adminEmail = localStorage.getItem("adminEmail");
        setIsAdmin(!!adminEmail);

        resetState();

        return () => window.removeEventListener("themeChanged", syncTheme);
    }, []);

    const resetState = () => {
        setInputText("");
        setLabel("");
        setConfidence("");
        setStatus("");
        setPlagScore(null);
        setPlagMatches([]);
        setVisibleMatches(5);
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setSelectedFileName(file.name);
        resetState();
        setStatus("Extracting text from file...");
        setLoading(true);

        try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await fetch(`${API_URL}/extract_text`, {
                method: "POST",
                body: formData,
            });

            if (!response.ok) throw new Error("File upload failed");

            const data = await response.json();

            setInputText(data.text || "");
            setStatus("Text extracted successfully!");
        } catch (err) {
            console.error(err);
            setStatus("Error uploading or extracting file.");
        } finally {
            setLoading(false);
        }
    };

    const analyzeText = async () => {
        const wordCount = inputText.trim().split(/\s+/).filter((w) => w.length > 0).length;

        if (wordCount < 40) {
            setStatus("Please enter at least 40 words to analyze.");
            return;
        }

        setLoading(true);
        setStatus("Analyzing...");

        // Clear previous results before new scan
        setLabel("");
        setConfidence("");
        setPlagScore(null);
        setPlagMatches([]);
        setVisibleMatches(5);

        try {
            const fetchAI = fetch(`${API_URL}/analyze`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify({ text: inputText }),
            });

            const fetchPlag = fetch(`${API_URL}/plagiarism-check`, {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify({ text: inputText }),
            });

            const [aiResponse, plagResponse] = await Promise.all([fetchAI, fetchPlag]);

            // Handle AI Response
            if (aiResponse.ok) {
                const aiData = await aiResponse.json();
                const conf = Number(aiData.confidence);
                setConfidence(conf.toFixed(0));
                setLabel(aiData.label);
                setResultColor(aiData.label === "Human Written" ? "#22c55e" : "#ef4444");
            } else {
                console.error("AI Analysis failed");
            }

            // Handle Plagiarism Response
            if (plagResponse.ok) {
                const plagData = await plagResponse.json();
                const score = (typeof plagData.score === 'number') ? plagData.score : 0;
                setPlagScore(score);
                setPlagMatches(plagData.matches || []);
            } else {
                console.error("Plagiarism Check failed");
            }

            setStatus("");

        } catch (err) {
            console.error("Error:", err);
            setStatus("Error connecting to backend.");
        } finally {
            setLoading(false);
        }
    };

    const clearText = () => {
        resetState();
        setSelectedFileName("");
        setInputMode("text");
    };

    // --- Chart Data Construction ---

    // AI Data
    const aiPercent = label === "AI Generated" ? confidence : 100 - (Number(confidence) || 0);
    const humanPercent = label === "Human Written" ? confidence : 100 - (Number(confidence) || 0);

    const aiDonutData = {
        labels: [`AI Generated ${Number(aiPercent).toFixed(2)}%`, `Human Written ${Number(humanPercent).toFixed(2)}%`],
        datasets: [{
            data: [aiPercent, humanPercent],
            backgroundColor: ["#ef4444", "#22c55e"],
            borderWidth: 0,
        }]
    };

    // Plagiarism Data
    const plagPercent = plagScore || 0;
    const uniquePercent = 100 - plagPercent;

    const plagDonutData = {
        labels: ["Plagiarized", "Unique"],
        datasets: [{
            data: [plagPercent, uniquePercent],
            backgroundColor: ["#f97316", "#16a34a"],
            borderWidth: 0,
        }]
    };

    const donutOptions = {
        cutout: "75%",
        plugins: {
            legend: { display: false },
            tooltip: { enabled: true },
        },
    };

    const hasResults = label && confidence && plagScore !== null;

    return (
        <div className={darkMode ? "app dark-mode" : "app"}>

            {/* Back button for admins */}
            {isAdmin && (
                <button
                    onClick={() => navigate("/admin")}
                    style={{
                        position: "fixed",
                        top: "80px",
                        left: "30px",
                        backgroundColor: "#636CCB",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        padding: "8px 16px",
                        cursor: "pointer",
                        fontWeight: "500",
                        fontSize: "0.9em",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                        width: "60px",
                        zIndex: 1000
                    }}
                >
                    ⮜
                </button>
            )}

            <div style={{ marginTop: "40px", textAlign: "center" }}>
                <h1 className="title" style={{ fontSize: "2rem", fontWeight: "bold", margin: 0 }}>
                    SourceCheck
                </h1>
            </div>

            {!hasResults && (
                <p className="subtitle">
                    Check your text for AI generation and plagiarism simultaneously.
                </p>
            )}

            <div className="container">
                {/* INPUT CARD - Only show if NO results */}
                {!hasResults && (
                    <div className="card input-card">
                        <p className="note">Enter at least 40 words to begin analysis.</p>

                        <div style={{ marginBottom: "15px", fontSize: "1rem" }}>
                            <label style={{ marginRight: "15px", cursor: "pointer" }}>
                                <input
                                    type="radio"
                                    value="text"
                                    checked={inputMode === "text"}
                                    onChange={() => { setInputMode("text"); setSelectedFileName(""); setInputText(""); }}
                                    style={{ marginRight: "6px" }}
                                />
                                Text
                            </label>
                            <label style={{ cursor: "pointer" }}>
                                <input
                                    type="radio"
                                    value="file"
                                    checked={inputMode === "file"}
                                    onChange={() => { setInputMode("file"); setInputText(""); }}
                                    style={{ marginRight: "6px" }}
                                />
                                File
                            </label>
                        </div>

                        {inputMode === "text" && (
                            <textarea
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                placeholder="Type or paste your text here..."
                                className="textarea"
                            />
                        )}

                        {inputMode === "file" && (
                            <div
                                style={{
                                    border: "2px dashed #b48fbf",
                                    borderRadius: "12px",
                                    padding: "35px",
                                    textAlign: "center",
                                    background: "#faf5ff",
                                    color: "#636CCB",
                                    cursor: "pointer",
                                    marginBottom: "10px",
                                    width: "100%",
                                    boxSizing: "border-box"
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    const file = e.dataTransfer.files[0];
                                    if (file) handleFileUpload({ target: { files: [file] } });
                                }}
                                onDragOver={(e) => e.preventDefault()}
                            >
                                <p>Click or Drag File Here</p>
                                <input
                                    type="file"
                                    accept=".txt,.pdf,.docx"
                                    onChange={handleFileUpload}
                                    style={{ display: "block", margin: "10px auto" }}
                                />
                                {selectedFileName && <p style={{ fontWeight: "bold" }}>{selectedFileName}</p>}
                            </div>
                        )}

                        {loading && <div className="spinner" />}
                        {status && <p className="status">{status}</p>}

                        <button
                            className="btn btn-analyze"
                            onClick={analyzeText}
                            disabled={loading}
                        >
                            {loading ? "Scanning..." : "Scan Text"}
                        </button>

                        <p onClick={clearText} style={{ marginTop: "12px", color: "#6366F1", cursor: "pointer", textDecoration: "underline", fontSize: "14px" }}>
                            Clear text
                        </p>
                    </div>
                )}

                {/* RESULTS SECTION - Split View (2 Columns) */}
                {hasResults && (
                    <div className="results-section">
                        {/* LEFT COLUMN: Analyzed Text & Feedback */}
                        <div className="result-column left-panel">
                            <div className="card text-viewer-card">
                                <h3 className="result-card-title" style={{ alignSelf: 'center', marginBottom: '15px' }}>Analyzed Text</h3>
                                <textarea
                                    className="analyzed-text-content"
                                    value={inputText}
                                    onChange={(e) => setInputText(e.target.value)}
                                />
                                <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                    <span style={{ fontSize: '0.9rem', color: 'var(--subtitle-light)' }}>
                                        {inputText.trim().split(/\s+/).length} words
                                    </span>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button onClick={analyzeText} className="btn btn-secondary">
                                            Scan Again
                                        </button>
                                        <button onClick={clearText} className="btn btn-secondary">
                                            Clear Text
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="card feedback-section">
                                <h3 className="feedback-title">Was SourceCheck helpful?</h3>
                                <div style={{ display: "flex", justifyContent: "center", gap: "20px" }}>
                                    <button
                                        onClick={() => { setLiked(true); setDisliked(false); }}
                                        className={`feedback-btn ${liked ? 'active' : ''}`}
                                        style={{ fontSize: "1.5em", background: "none", border: "none", cursor: "pointer", opacity: liked ? 1 : 0.6 }}
                                        title="Helpful"
                                    >
                                        👍
                                    </button>
                                    <button
                                        onClick={() => { setDisliked(true); setLiked(false); }}
                                        className={`feedback-btn ${disliked ? 'active' : ''}`}
                                        style={{ fontSize: "1.5em", background: "none", border: "none", cursor: "pointer", opacity: disliked ? 1 : 0.6 }}
                                        title="Not Helpful"
                                    >
                                        👎
                                    </button>
                                    <button
                                        onClick={() => setShowCommentModal(true)}
                                        className="feedback-btn"
                                        style={{ fontSize: "1.3em", color: "#64748b", background: "none", border: "none", cursor: "pointer" }}
                                        title="Leave a comment"
                                    >
                                        <FaCommentDots />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT COLUMN: Analysis Results (AI + Plagiarism + Matches) */}
                        <div className="result-column right-panel">
                            {/* AI Analysis */}
                            <div className="card result-card detailed-card">
                                <div className="chart-section">
                                    <div className="chart-container-small">
                                        <Doughnut data={aiDonutData} options={donutOptions} />
                                        <div className="chart-center-text-small" style={{ color: resultColor }}>
                                            {Number(confidence).toFixed(2).replace(/\.00$/, '')}%
                                        </div>
                                    </div>
                                </div>
                                <div className="details-section">
                                    <h3 className="result-card-title">AI Detection Results</h3>

                                    <div className="metric-row">
                                        <span className="metric-label">Status:</span>
                                        <span className={`status-badge ${label === "Human Written" ? "badge-human" : "badge-ai"}`}>
                                            {label === "Human Written" ? "Likely Human" : "Likely AI"}
                                        </span>
                                    </div>

                                    <div className="metric-row">
                                        <span className="metric-label">AI Probability:</span>
                                        <span className="metric-value" style={{ color: "#ef4444" }}>{Number(aiPercent).toFixed(2).replace(/\.00$/, '')}%</span>
                                    </div>

                                    <div className="metric-row">
                                        <span className="metric-label">Human Probability:</span>
                                        <span className="metric-value" style={{ color: "#22c55e" }}>{Number(humanPercent).toFixed(2).replace(/\.00$/, '')}%</span>
                                    </div>

                                    <div className="metric-row">
                                        <span className="metric-label">Confidence Level:</span>
                                        <span className="metric-value">{Number(confidence).toFixed(2).replace(/\.00$/, '')}%</span>
                                    </div>
                                </div>
                            </div>

                            {/* Plagiarism Analysis */}
                            <div className="card result-card detailed-card">
                                <div className="chart-section">
                                    <div className="chart-container-small">
                                        <Doughnut data={plagDonutData} options={donutOptions} />
                                        <div className="chart-center-text-small" style={{ color: plagScore > 0 ? "#ef4444" : "#22c55e" }}>
                                            {Number(plagScore).toFixed(2).replace(/\.00$/, '')}%
                                        </div>
                                    </div>
                                </div>
                                <div className="details-section">
                                    <h3 className="result-card-title">Plagiarism Analysis</h3>

                                    <div className="metric-row">
                                        <span className="metric-label">Status:</span>
                                        <span className={`status-badge ${plagScore > 0 ? "badge-ai" : "badge-human"}`}>
                                            {plagScore > 0 ? "Plagiarized" : "Unique"}
                                        </span>
                                    </div>

                                    <div className="metric-row">
                                        <span className="metric-label">Plagiarized:</span>
                                        <span className="metric-value" style={{ color: "#f97316" }}>{Number(plagPercent).toFixed(2).replace(/\.00$/, '')}%</span>
                                    </div>

                                    <div className="metric-row">
                                        <span className="metric-label">Unique:</span>
                                        <span className="metric-value" style={{ color: "#16a34a" }}>{Number(uniquePercent).toFixed(2).replace(/\.00$/, '')}%</span>
                                    </div>
                                </div>
                            </div>

                            {/* Plagiarism Matches */}
                            {plagMatches.length > 0 && (
                                <div className="card plag-matches-container">
                                    <p className="plag-matches-title">Matches Found:</p>
                                    {plagMatches.map((m, i) => (
                                        <div key={i} className="plag-match-item">
                                            <p className="plag-match-text">"{m.phrase}"</p>
                                            <a
                                                href={`${m.link}#:~:text=${(() => {
                                                    const words = m.phrase.split(/\s+/);
                                                    if (words.length > 8) {
                                                        const start = words.slice(0, 4).join(' ');
                                                        const end = words.slice(-4).join(' ');
                                                        return `${encodeURIComponent(start)},${encodeURIComponent(end)}`;
                                                    }
                                                    return encodeURIComponent(m.phrase);
                                                })()}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="plag-source-link"
                                            >
                                                Source Link
                                            </a>

                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {showCommentModal && (
                <div className={darkMode ? "modal-overlay dark-mode" : "modal-overlay"}>
                    <div className="modal-content">
                        {feedbackSubmitted ? (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "15px",
                                    padding: "20px 10px",
                                }}
                            >
                                <h3 className="modal-title">
                                    Thank you for your feedback!
                                </h3>
                            </div>
                        ) : (
                            <>
                                <h3 className="modal-title">
                                    Share your feedback
                                </h3>

                                <textarea
                                    ref={textareaRef}
                                    placeholder="Write your comment here..."
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    className="modal-textarea"
                                />

                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        gap: "15px",
                                    }}
                                >
                                    <button
                                        onClick={async () => {
                                            try {
                                                const feedbackData = {
                                                    user_id: localStorage.getItem("user_id"),
                                                    username: userName || "Admin",
                                                    rating: liked ? 5 : disliked ? 1 : 3,
                                                    comment: comment || "No comment",
                                                    inputType: selectedFileName ? "file" : "text",
                                                    analyzedText: inputText || "",
                                                    output: {
                                                        activeTab,
                                                        label,
                                                        confidence,
                                                        plagScore,
                                                        plagMatches,
                                                    },
                                                };

                                                await fetch(`${BACKEND_URL}/feedback`, {
                                                    method: "POST",
                                                    headers: getAuthHeaders(),
                                                    body: JSON.stringify(feedbackData),
                                                });

                                                setFeedbackSubmitted(true);
                                                setTimeout(() => {
                                                    setShowCommentModal(false);
                                                    setFeedbackSubmitted(false);
                                                    setComment("");
                                                    setLiked(false);
                                                    setDisliked(false);
                                                }, 1000);
                                            } catch (err) {
                                                console.error("Error sending feedback:", err);
                                            }
                                        }}
                                        className="btn btn-analyze"
                                        style={{ marginTop: 0 }}
                                    >
                                        Submit
                                    </button>

                                    <button
                                        onClick={() => setShowCommentModal(false)}
                                        className="modal-btn-cancel"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}